import { Link } from 'gatsby';
import type { MouseEvent, MouseEventHandler, ReactNode } from 'react';
import AnalyticsService, {
    GTM_ANALYTICS_EVENTS
} from '../../Services/AnalyticsService';
import { pagesSet } from '../../../pages-list';
import { checkShouldReloadDueToMissingTestsInTargetPage } from '../../ab-tests/ab-test-routing-utils';
import { getFinalUrl } from '../../constants/webflow-path-mappings';

interface Props {
    id?: string;
    link: string;
    role?: string;
    dataLinkKey?: string;
    tabIndex?: number;
    className?: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
    onMouseEnter?: MouseEventHandler<HTMLAnchorElement>;
    children?: ReactNode;
    newTab?: boolean;
    source:
        | 'logo'
        | 'navbar'
        | 'footer'
        | 'pop_up'
        | 'navbar_menu'
        | 'feature_values'
        | 'invoice_template_block'
        | 'tos'
        | '404_to_home'
        | '404_to_recovery'
        | 'unsupported_to_home'
        | 'promo banner'
        | 'online_invoices_hero'
        | 'template-preview';
}

const NavigateLink = ({
    id,
    className = '',
    link,
    onClick,
    onMouseEnter,
    role,
    tabIndex,
    dataLinkKey = 'data-item-link',
    children,
    source,
    newTab = false
}: Props) => {
    const _onClick = (event: MouseEvent<HTMLAnchorElement>) => {
        AnalyticsService.trackGtm(GTM_ANALYTICS_EVENTS.navigation_click, {
            click_url: link,
            section: source
        });
        onClick?.(event);

        const shouldReload =
            checkShouldReloadDueToMissingTestsInTargetPage(link);
        if (shouldReload) {
            window.location.assign(link);
            event.preventDefault();
            return;
        }
    };

    // Get the final URL (Gatsby or Webflow based on build-time flag)
    const finalUrl = getFinalUrl(link);
    const isExternalLink = finalUrl.startsWith('http');

    const attributesForLink = {
        className,
        onClick: _onClick,
        onMouseEnter,
        [dataLinkKey]: link, // Keep original link for analytics
        tabIndex,
        target: newTab ? '_blank' : '_self'
    };

    if (role) {
        attributesForLink['role'] = role;
    }
    if (id) {
        attributesForLink['data-id'] = id;
    }

    // If it's an external link (including webflow paths), use anchor tag
    if (isExternalLink) {
        return (
            <a href={finalUrl} {...attributesForLink}>
                {children}
            </a>
        );
    }

    // For internal Gatsby paths, use Gatsby Link
    return pagesSet.has(link) ? (
        <Link to={link} {...attributesForLink}>
            {children}
        </Link>
    ) : (
        <a href={finalUrl} {...attributesForLink}>
            {children}
        </a>
    );
};

export default NavigateLink;
